import type { CookieCategory, IConsentPreferences } from '../api/types'

/**
 * localStorage key for the granular consent record (v2+).
 * Shape: {@link IConsentRecord} serialized as JSON.
 */
export const CONSENT_STORAGE_KEY = 'ma-cookie-preferences'

/**
 * Legacy key used by the first cookie banner (v1). Its value was the
 * bare string 'accepted' — no categories, no timestamp. We read it only
 * to migrate existing users into the granular record and then clear it.
 */
export const LEGACY_CONSENT_KEY = 'ma-cookie-consent'
export const LEGACY_ACCEPTED_VALUE = 'accepted'

/**
 * Current schema version. Bump when the shape of {@link IConsentPreferences}
 * changes (new category added, existing one split). The store ignores
 * persisted records with a version it does not understand.
 */
export const CONSENT_SCHEMA_VERSION = 2

/**
 * Defaults used when the user has not yet made a decision. Reject-by-default
 * is the GDPR-aligned stance: anything non-essential stays off until the
 * user actively turns it on.
 */
export const DEFAULT_PREFERENCES = Object.freeze({
  necessary: true as const,
  functional: false,
  analytics: false,
  marketing: false,
}) satisfies IConsentPreferences

/**
 * What "Accept all" writes. Kept separate from the defaults so the two
 * intents — "no decision yet" and "user consented to everything" — do
 * not get tangled.
 */
export const FULL_CONSENT_PREFERENCES = Object.freeze({
  necessary: true as const,
  functional: true,
  analytics: true,
  marketing: true,
}) satisfies IConsentPreferences

/**
 * Ordered list of categories shown in the Preferences UI. Necessary is
 * first so users immediately see what they cannot disable and why.
 */
export const CATEGORY_ORDER = Object.freeze([
  'necessary',
  'functional',
  'analytics',
  'marketing',
]) satisfies readonly CookieCategory[]
